import React, { Fragment, useState } from 'react';
import { PageTitle } from '../../../components/PageTitle';
import { useTranslation } from 'react-i18next';

import { useLocation, useNavigate } from 'react-router';
import { FloatingActionsButtons } from '../../../components/FloatingActionsButtons';
import { CreateAction } from '../../../components/FloatingActionsButtons/components/CreateAction';
import { ManagedDataTable } from '../../../components/ManagedDataTable';
import {
  ColumnFilters,
  IManagedDataTable,
} from '../../../components/ManagedDataTable/interfaces';
import { DATA_TABLE_STORAGE_KEYS } from '../../../constants';
import { ROUTES } from '../../../routes/Routes';
import { useGetAgentsList } from '../../../reactQuery/Agents/agents';
import {
  useGetOpportunities,
  useGetStages,
} from '../../../reactQuery/opportunities';
import { Item, Status } from '../../../api/opportunitiesApi/interfaces';
import { getColumns } from '../../../components/tabs/tab-panels/OpportunitiesTabPanel/helpers';
import { useGetClientsList } from '../../../reactQuery/clients';
import { useGetContactsList } from '../../../reactQuery/contacts';
import { useGetproductsList } from '../../../reactQuery/products';
import { UpsertItemDialog } from '../dialogs/UpsertItemDialog';

export const OpportunitiesOverview: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);

  const { data: agentsList } = useGetAgentsList();
  const { data: clientsList } = useGetClientsList();
  const { data: contactsList } = useGetContactsList();
  const { data: productsList } = useGetproductsList();
  const { data: stagesList } = useGetStages();

  const onRowClick = (row: Item): void => {
    navigate(ROUTES.opportunities.details(row.id), {
      state: { from: location },
    });
  };

  const columnFilters: ColumnFilters = [
    {
      column: 'status',
      label: t('entities:opportunityItem.status'),
      type: 'multivalue',
      options: Object.values(Status).map((statusKey) => ({
        id: statusKey,
        name: t(`entities:opportunityItem.enums.status.${statusKey}`),
      })),
    },
    {
      column: 'stage',
      label: t('entities:opportunityItem.stage'),
      type: 'multivalue',
      options:
        stagesList?.map((stage) => {
          return {
            id: stage.id,
            name: stage.name,
          };
        }) || [],
    },
    {
      column: 'client',
      label: t('entities:opportunityItem.client'),
      type: 'multivalue',
      options: clientsList || [],
    },
    {
      column: 'contact',
      label: t('entities:opportunityItem.contact'),
      type: 'multivalue',
      options:
        contactsList?.map((agent) => {
          return {
            id: agent.id,
            name: `${agent.firstName} ${agent.lastName}`,
          };
        }) || [],
    },
    {
      column: 'agent',
      label: t('entities:opportunityItem.agent'),
      type: 'multivalue',
      options:
        agentsList?.map((agent) => {
          return {
            id: agent.id,
            name: `${agent.firstName} ${agent.lastName}`,
          };
        }) || [],
    },
    {
      column: 'product',
      label: t('entities:opportunityItem.product'),
      type: 'multivalue',
      options:
        productsList?.map((product) => {
          return {
            id: product.id,
            name: product.name,
          };
        }) || [],
    },
    {
      column: 'createdAt',
      label: t('entities:opportunityItem.createdAt'),
      type: 'date',
    },
    {
      column: 'closedAt',
      label: t('entities:opportunityItem.closedAt'),
      type: 'date',
    },
  ];

  return (
    <Fragment>
      <PageTitle
        title={t('opportunities:overview.title')}
        subTitle={t('opportunities:overview.subTitle')}
        space
      />
      <ManagedDataTable
        query={useGetOpportunities as IManagedDataTable<Item>['query']}
        columns={getColumns()}
        onRowClick={onRowClick}
        storageKey={DATA_TABLE_STORAGE_KEYS.opportunities}
        initialSort={{ field: 'createdAt', sort: 'desc' }}
        columnFilters={columnFilters}
      />
      <UpsertItemDialog
        show={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
        }}
      />
      <FloatingActionsButtons>
        <CreateAction onClick={() => setShowCreateDialog(true)} />
      </FloatingActionsButtons>
    </Fragment>
  );
};
